import { Cart, MainProduct, PendantProduct, SinglePendantProductVariant } from "../types"

export const isEditMode = () => {
  return new URLSearchParams(window.location.search).has("edit_qts")
}

export const getEditModeItems = (_mainProduct: MainProduct, cart: Cart) => {
  const url = new URL(window.location.href)
  const urlSearchParams = url.searchParams
  const urlQueryUniqueTimeStamp = urlSearchParams.get('edit_qts')

  if (!urlQueryUniqueTimeStamp) return null

  const mainProductVariantIdItem = cart.items.find(item => 
    item.properties._uniqueTimeStamp === urlQueryUniqueTimeStamp && 
    item.properties._NecklaceId === undefined
  )!
  const pendantVariantIdItems = cart.items.filter(item => 
    item.properties._uniqueTimeStamp === urlQueryUniqueTimeStamp && 
    item.properties._NecklaceId !== undefined
  )

  return ({
    chain: mainProductVariantIdItem,
    pendants: pendantVariantIdItems
  })
}

export const areArraysEqual = (a: string[], b: string[]) => {
  return (
    a.length === b.length && 
    a.every((value, index) => value === b[index])
  )
}

export const getActiveLengthByMainProductVariantId = (
  mainProduct: MainProduct,
  mainProductVariantId: string
) => {
  const mainProductVariant = mainProduct.variants
    .find(mainProductVariant => (Number(mainProductVariant.id) === Number(mainProductVariantId)))

  if (!mainProductVariant) {
    throw new Error(`Main product variant with id ${mainProductVariantId} not found`)
  }

  const lengthOptionIndex = getProductOptionNameIndexByName(mainProduct, "Length")

  const length = mainProductVariant.options[lengthOptionIndex]

  return length
}

export const getSinglePendantMainProductVariant = (
  mainProduct: MainProduct, 
  selectedMainProductVariantId: number, 
  selectedPendantProductVariantId: number
) => {
  const variantToAdd = getOptionalSinglePendantMainProductVariant(
    mainProduct,
    selectedMainProductVariantId,
    selectedPendantProductVariantId
  )

  if (!variantToAdd) {
    throw new Error(`Cannot find main product variant linked to pendant product variant id: ${selectedPendantProductVariantId}`)
  }

  return variantToAdd
}

export const getOptionalSinglePendantMainProductVariant = (
  mainProduct: MainProduct, 
  selectedMainProductVariantId: number, 
  selectedPendantProductVariantId: number
) => {
  const lengthOptionIndex = mainProduct.optionsWithValues.findIndex(optionWithValue => optionWithValue.name === 'Length')
  if (lengthOptionIndex === -1) {
    throw new Error('Length option is not found')
  }

  const selectedMainProductVariant = mainProduct.variants.find(variant => +variant.id === selectedMainProductVariantId)
  if (!selectedMainProductVariant) {
    throw new Error(`Main product variant does not exist. id: ${selectedMainProductVariantId}`)
  }
  
  const selectedLength = selectedMainProductVariant.options[lengthOptionIndex]
  const selectedPendantId = selectedPendantProductVariantId

  const variantToAdd = mainProduct.variants.find(variant => {
    return (
      variant.metafields.linkedPendantProductVariant?.id === +selectedPendantId &&
      variant.options[lengthOptionIndex] === selectedLength
    )
  })

  return variantToAdd
}

export const getSelectedPurchasableMainProductVariant = (
  mainProduct: MainProduct,
  activeMainProductVariantId: string,
  activePendantProductVariantIds: string[],
  isMainProductSinglePendant: boolean
) => {
  if (isMainProductSinglePendant) {
    return getOptionalSinglePendantMainProductVariant(
      mainProduct,
      Number(activeMainProductVariantId),
      Number(activePendantProductVariantIds[0])
    )
  }

  return getMainProductVariantById(mainProduct, activeMainProductVariantId)
}

export const isPendantVariantOutOfStock = (
  mainProduct: MainProduct,
  activeMainProductVariantId: string,
  pendantProductVariantId: string,
  isMainProductSinglePendant: boolean
) => {
  const pendantProductVariant = getPendantProductVariantById(mainProduct, pendantProductVariantId)

  if (!pendantProductVariant.available) {
    return true
  }

  if (!isMainProductSinglePendant) {
    return false
  }

  const purchasableVariant = getOptionalSinglePendantMainProductVariant(
    mainProduct,
    Number(activeMainProductVariantId),
    Number(pendantProductVariantId)
  )

  return !purchasableVariant?.available
}

export const getMainProductVariantById = (mainProduct: MainProduct, mainProductVariantId: string) => {
  const mainProductVariant = mainProduct.variants
    .find(mainProductVariant => String(mainProductVariant.id) === String(mainProductVariantId))!

  return mainProductVariant
}

export const isSinglePendantProduct = (mainProduct: MainProduct) => {
  const hasSinglePendantProducts = (mainProduct.metafields.singlePendantProducts || []).some(
    (singlePendantProduct) => singlePendantProduct.variants.length > 0
  )
  const hasLinkedPendantVariants = mainProduct.variants.some(
    (variant) => variant.metafields.linkedPendantProductVariant?.id
  )

  return mainProduct.metafields.builderData.maxNumberOfPendants === 1 && (hasSinglePendantProducts || hasLinkedPendantVariants)
}

export const getProductOptionNameIndexByName = (product: MainProduct | PendantProduct, optionName: string) => {
  if (!product) {
    throw new Error(`Cannot resolve option \"${optionName}\" because the product payload is missing.`)
  }

  const productOptionNameIndexByName  = product.optionsWithValues
    .find((optionWithValues) => optionWithValues.name === optionName)!
    .position - 1

  return productOptionNameIndexByName
}

export const getActiveMaterialByMainProductVariantId = (
  mainProduct: MainProduct,
  mainProductVariantId: string
) => {
  const mainProductVariant = mainProduct.variants
    .find(mainProductVariant => (String(mainProductVariant.id) === String(mainProductVariantId)))!
  
  const materialOptionIndex = getProductOptionNameIndexByName(mainProduct, "Metal")

  const material = mainProductVariant.options[materialOptionIndex]

  return material
}

export const getCorrespondingPendantProductVariantIdsByMainProductVariantId = (
  mainProduct: MainProduct,
  mainProductVariantId: string,
  activePendantProductVariantIds: string[],
) => {
  const mainProductVariant = mainProduct.variants
    .find(mainProductVariant => (Number(mainProductVariant.id) === Number(mainProductVariantId)))

  if (!mainProductVariant) {
    throw new Error(`Main product variant with id ${mainProductVariantId} not found`)
  }
  
  const materialOptionIndex = getProductOptionNameIndexByName(mainProduct, "Metal")

  const material = mainProductVariant.options[materialOptionIndex]

  const correspondingPendantProductVariantIdsByMaterial = activePendantProductVariantIds
    .map(activePendantProductVariantId => {
      const pendantProduct = mainProduct.metafields.pendantProducts
        .find(pendantProduct => pendantProduct.variants.
          some(pendantProductVariant => Number(pendantProductVariant.id) === Number(activePendantProductVariantId))
        )
      
      if (!pendantProduct) {
        throw new Error(`Pendant product variant with id ${activePendantProductVariantId} not found`)
      }
      
      const pendantProductVariant = pendantProduct.variants
        .find(pendantProductVariant => Number(pendantProductVariant.id) === Number(activePendantProductVariantId))!
      
      const pendantProductMaterialOptionIndex = getProductOptionNameIndexByName(pendantProduct, "Metal")

      const optionsInCorrespondingPendantProductVariant = [
        ...pendantProductVariant.options.slice(0, pendantProductMaterialOptionIndex),
        material,
        ...pendantProductVariant.options.slice(pendantProductMaterialOptionIndex + 1, pendantProductVariant.options.length)
      ]

      const correspondingPendantProductVariant = pendantProduct.variants
        .find(pendantProductVariant => areArraysEqual(pendantProductVariant.options, optionsInCorrespondingPendantProductVariant))
      
      if (!correspondingPendantProductVariant) {
        throw new Error(`Corresponding pendant product variant for material ${material} not found`)
      }
      
      return correspondingPendantProductVariant.id
    })

  return correspondingPendantProductVariantIdsByMaterial
}

export const getInitialActivePendantProductVariantIds = (
  mainProduct: MainProduct,
  activeMainProductVariantId: string
) => {
  if (isSinglePendantProduct(mainProduct)) {
    const activeMainProductVariant = mainProduct.variants.find(variant => String(variant.id) === String(activeMainProductVariantId))
      ?? mainProduct.variants.find(variant => variant.available)
      ?? mainProduct.variants[0]

    if (!activeMainProductVariant) {
      throw new Error("No main product variant found")
    }

    const linkedPendantProductVariant = activeMainProductVariant.metafields.linkedPendantProductVariant

    if (!linkedPendantProductVariant) {
      throw new Error("Linked pendant product variant not found for main product variant id " + activeMainProductVariantId)
    }

    return [String(linkedPendantProductVariant.id)]
  }

  const getPendantVariantIdByPendantAndMetal = (
    pendantProduct: PendantProduct,
    material: string,
    index: number
  ) => {
    const pendantProductMaterialOptionIndex = getProductOptionNameIndexByName(pendantProduct, "Metal")

    const matchingVariants = pendantProduct.variants
      .filter(pendantProductVariant => (
        pendantProductVariant.options[pendantProductMaterialOptionIndex] === material && pendantProductVariant.available
      ))

    if (matchingVariants.length === 0) {
      throw new Error(`No available variants found with the following material: ${material}`)
    }

    for (let i = index; i >= 0; i--) {
      if (i < matchingVariants.length) {
        return matchingVariants[i]!.id
      }
    }

    return matchingVariants[0]!.id
  }

  const material = getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId)
  const pendantProducts = mainProduct.metafields.pendantProducts || []

  if (pendantProducts.length === 0) {
    return []
  }

  const firstPendantProduct = pendantProducts[0]
  const secondPendantProduct = pendantProducts[1]

  if (firstPendantProduct && secondPendantProduct) {
    return [
      getPendantVariantIdByPendantAndMetal(firstPendantProduct, material, 0),
      getPendantVariantIdByPendantAndMetal(secondPendantProduct, material, 2)
    ]
  }

  return [
    getPendantVariantIdByPendantAndMetal(firstPendantProduct, material, 0),
    getPendantVariantIdByPendantAndMetal(firstPendantProduct, material, 0)
  ]
}

export const getFirstAvailablePendantVariantIdByMetalAndPendantProductId = (
  mainProduct: MainProduct,
  material: string, 
  pendantProductId: string
) => {
  const firstPendantProduct = mainProduct.metafields.pendantProducts.find(pendantProduct => String(pendantProduct.id) === String(pendantProductId))!
  const pendantProductMaterialOptionIndex = getProductOptionNameIndexByName(firstPendantProduct, "Metal")

  const firstAvailablePendantProductVariantByMaterial = firstPendantProduct.variants
    .find(pendantProductVariant => (
      pendantProductVariant.options[pendantProductMaterialOptionIndex] === material
    ))!
    .id

  return firstAvailablePendantProductVariantByMaterial
}

// @TODO: 
// 1. Make common utility functions
// 2. Move them to AppContext, not to pass mainProduct over and over again
// 3. Use them instead of specific utility function in every single component

export const getPendantProductVariantById = (mainProduct: MainProduct, pendantProductVariantId: string) => {
  const pendantProductVariant = mainProduct.metafields.pendantProducts
    .flatMap(pendantProduct => pendantProduct.variants)
    .find(pendantProductVariant => String(pendantProductVariant.id) === String(pendantProductVariantId))!

  return pendantProductVariant
}

export const getPendantProductByPendantProductVariantId = (
  mainProduct: MainProduct, 
  pendantProductVariantId: string
) => {
  const pendantProductVariant = mainProduct.metafields.pendantProducts
    .find(pendantProduct => 
        pendantProduct.variants
          .some(pendantProductVariant => String(pendantProductVariant.id) === String(pendantProductVariantId))
    )!

  return pendantProductVariant
}

export const getSingleProductVariantFromMainProductByLengthInTitle = (
  mainProduct: MainProduct, 
  activeMainProductVariantId: number,
  activePendantProductVariantId: number
) => {
  const singlePendantProductVariants: SinglePendantProductVariant[] = (mainProduct.metafields.singlePendantProducts || [])
    .flatMap(product => product.variants)

  const activeLength = getActiveLengthByMainProductVariantId(mainProduct, String(activeMainProductVariantId))
  const linkedSingleProductVariant = singlePendantProductVariants
    .filter(singlePendantProductVariant => singlePendantProductVariant.title.split("/")[1].includes(activeLength))
    .find(singlePendantProductVariant => 
      singlePendantProductVariant.metafields.linkedPendantProductVariant?.id === activePendantProductVariantId
    )
  return linkedSingleProductVariant
}

export const getTotalPrice = (mainProduct: MainProduct, mainProductVariantId: string, pendantProductVariantIds: string[]) => {
  const mainProductVariantPrice = mainProduct.variants.find(mainProductVariant => Number(mainProductVariant.id) === Number(mainProductVariantId))!.price
  
  const isMainProductSinglePendant = isSinglePendantProduct(mainProduct)
  if (isMainProductSinglePendant) {
    const mainProductSinglePendantVariant = getOptionalSinglePendantMainProductVariant(
      mainProduct,
      Number(mainProductVariantId),
      Number(pendantProductVariantIds[0])
    )

    return mainProductSinglePendantVariant?.price ?? mainProductVariantPrice
  }

  const pendantProductVariants = mainProduct.metafields.pendantProducts.flatMap(pendantProduct => pendantProduct.variants)
  const pendantsTotalPrice = pendantProductVariantIds.reduce((pendantsTotalPrice, pendantProductVariantId) => {
    return pendantsTotalPrice + pendantProductVariants.find(pendantProductVariant => Number(pendantProductVariant.id) === Number(pendantProductVariantId))!.price
  }, 0)

  const totalPrice = mainProductVariantPrice + pendantsTotalPrice

  return totalPrice
}
